import PopupBasic from 'src/game/components/popups/PopupBasic';
import i18n from 'src/lib/i18n/i18n';
import uiConfig from 'src/lib/ui/config';
import ButtonScaleViewWithText from 'src/lib/ui/components/ButtonScaleViewWithText';
import View from '@play-co/timestep-core/lib/ui/View';
import { animDuration, delay } from 'src/lib/utils';
import sounds from 'src/lib/sounds';
import { Feature } from 'src/replicant/analytics';
import { PetEntity } from 'src/game/logic/EntityManager';
import { PetSpeciesID } from 'src/replicant/pet';
import * as Pet from 'src/replicant/pet';
import { petAnimationNames } from 'src/replicant/pet/PetAssets';
import { LangBitmapFontTextShadowView } from 'src/lib/ui/components/LangBitmapFontTextShadowView';
import { gsap } from 'gsap';
import { user } from 'src/StateObserver';
import Match2FreeMovesPanel from './Match2FreeMovesPanel';
import ButtonScaleView from 'src/lib/ui/components/ButtonScaleView';
import { getMatch2ContinueConfig } from 'src/replicant/match2/match2.getters';
import {
  getMatch2CloseButtonX,
  match2CloseButtonY,
} from './match2PopupCloseButton';
const ui = uiConfig.buruburu.care.confirmation;

export default class PopupMatch2FreeContinue extends PopupBasic {
  private contentWrapper: View;
  private movesPanel: Match2FreeMovesPanel;
  private pet: PetEntity;
  private species: PetSpeciesID | undefined;
  private toggleMessage?: LangBitmapFontTextShadowView;
  private isPopupHidden = false;

  constructor(opts: { superview: View; close: (success: boolean) => void }) {
    const offset = 140;
    super({
      superview: opts.superview,
      close: () => opts.close(false),
      ...ui.popup,
      height: ui.popup.height - offset,
      tracking: {
        feature: Feature.MATCH2._,
        $subFeature: Feature.MATCH2.FREE_CONTINUE_POPUP,
      },
    });

    this.buttonClose.removeFromSuperview();
    this.message.removeFromSuperview();
    this.title.title.updateOpts({ color: '#E25959' });
    this.bg.updateOpts({
      backgroundColor: 'rgba(71,24,67,0.95)',
    });
    this.bg.onClick = () => this.toggleShowPuzzle();

    new ButtonScaleView({
      ...uiConfig.buttons.close,
      superview: this.box,
      x: getMatch2CloseButtonX(this.box.style.width),
      y: match2CloseButtonY,
      onClick: async () => {
        sounds.playSound('cancel');
        return opts.close(false);
      },
      tracking: {
        feature: Feature.MATCH2._,
        $subFeature: Feature.MATCH2.FREE_CONTINUE_BACK,
      },
    });

    this.contentWrapper = new View({
      superview: this.box,
      width: this.box.style.width,
      height: this.box.style.height,
    });

    this.species = Pet.getters.getCurrentEvolution(user())?.species;
    this.pet = new PetEntity('minigame', this.species);
    this.pet.updateOpts({
      superview: this.contentWrapper,
      x: 140,
      y: 300,
      scale: 0.75,
      anchorX: 0.5 * this.pet.style.width,
      anchorY: 0.78 * this.pet.style.height,
    });

    this.movesPanel = new Match2FreeMovesPanel({
      superview: this.contentWrapper,
      x: 395,
      y: 205,
    });

    // Continue button
    new ButtonScaleViewWithText({
      ...ui.okButton,
      superview: this.contentWrapper,
      width: 450,
      height: 100,
      x: this.box.style.width * 0.5 - 450 * 0.5,
      y: ui.okButton.y - offset - 20,
      localeText: () => i18n('match2.movesPinch.titleFree'),
      tracking: {
        feature: Feature.MATCH2._,
        $subFeature: Feature.MATCH2.FREE_CONTINUE_OK,
      },
      onClick: async () => {
        sounds.sfx.play('ok');
        opts.close(true);
      },
    });
  }

  protected getAssetsGroup() {
    return null;
  }

  public init({ inventory, moves }: { inventory: number; moves: number }) {
    super.init({ title: i18n('match2.movesPinch.titleFree') });
    const petAnimations = petAnimationNames[this.species ?? 'ball'];

    this.pet.setAnimation(petAnimations.play);
    this.movesPanel.init(inventory, moves);
    this.toggleMessage = new LangBitmapFontTextShadowView({
      superview: this.root,
      font: 'Body',
      color: '#FFFFFF',
      width: this.root.style.width,
      height: 100,
      size: 30,
      x: this.root.style.width * 0.5,
      y: 100 + this.box.style.y + this.box.style.height * 0.5 + 100,
      localeText: () => i18n('match2.movesPinch.toggleMessage1'),
      centerOnOrigin: true,
      centerAnchor: true,
      verticalAlign: 'center',
      align: 'center',
      scaleY: 0,
      blockEvents: true,
      backgroundColor: 'rgba(0,0,0,0.7)',
    });
  }

  private async toggleShowPuzzle() {
    if (!this.toggleMessage) return;
    this.isPopupHidden = !this.isPopupHidden;

    // Toggle CTAs interactivity accordingly
    this.box.updateOpts({ blockEvents: this.isPopupHidden });

    const duration = 0.5;
    gsap.to(this.bg.style, {
      duration,
      opacity: this.isPopupHidden ? 0 : 1,
      overwrite: true,
    });
    gsap.to(this.box.style, {
      duration,
      opacity: this.isPopupHidden ? 0 : 1,
      overwrite: true,
    });

    await this.tweenToggleMessageScaleY(0);
    this.toggleMessage.localeText = () =>
      i18n(`match2.movesPinch.toggleMessage${this.isPopupHidden ? 2 : 1}`);
    await this.tweenToggleMessageScaleY(1);
  }

  private async tweenToggleMessageScaleY(v: number) {
    if (!this.toggleMessage) return;
    await gsap.to(this.toggleMessage.style, {
      duration: 0.15,
      scaleY: v,
      ease: 'quad.inOut',
      overwrite: true,
    });
  }

  public override async fadeIn() {
    super.fadeIn();
    await delay(animDuration * 2);
    await this.tweenToggleMessageScaleY(1);
  }

  public override async fadeOut() {
    void this.tweenToggleMessageScaleY(0);
    super.fadeOut();
    await delay(animDuration * 2);
  }
}
